import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNavigation } from '../shared/hooks/useNavigation'
import { ROUTES } from '../shared/navigation'
import { Mail, Lock, Eye, EyeOff, Phone, User, Calendar } from '../components/icons'
import { useAuthStore } from '../stores/authStore'
import { authService } from '../services/authService'
import { companyService } from '../services/companyService'
import { normalizePhoneNumber } from '../utils/phoneUtils'
import { handleBirthdayInput, formatBirthdayForBackend, validateBirthday } from '../utils/brazilianUtils'

const RegisterPage = () => {
  const { navigate } = useNavigation()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('token')
  const isInviteMode = !!inviteToken // true if user arrived via invite link

  const { login, isLoading, setLoading } = useAuthStore()

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [birthday, setBirthday] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [companyName, setCompanyName] = useState('Nome do Salao')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Fetch company information when component mounts
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const companyInfo = await companyService.getCompanyInfo()
        if (companyInfo.name) {
          setCompanyName(companyInfo.name)
        }
      } catch (error) {
        // Ignore company info fetch errors - use default name
      }
    }

    fetchCompanyInfo()
  }, [])

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    const input = e.target.value
    // Only allow digits, spaces, parentheses, and hyphens
    const cleaned = input.replace(/[^\d\s()-]/g, '')

    // Auto-format as Brazilian phone number
    const digits = cleaned.replace(/\D/g, '')
    if (digits.length <= 11) {
      if (digits.length <= 2) {
        setPhoneNumber(digits.length > 0 ? `(${digits}` : '')
      } else if (digits.length <= 7) {
        setPhoneNumber(`(${digits.slice(0, 2)}) ${digits.slice(2)}`)
      } else {
        setPhoneNumber(`(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`)
      }
    }
  }

  const validateForm = () => {
    // In invite mode, only validate password fields
    if (!isInviteMode) {
      if (!fullName.trim()) {
        setError('Por favor, informe seu nome completo.')
        return false
      }

      if (!email.trim()) {
        setError('Por favor, informe seu e-mail.')
        return false
      }

      // Basic email validation
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailPattern.test(email)) {
        setError('Por favor, informe um e-mail valido.')
        return false
      }

      // Validate birthday if provided
      if (birthday) {
        const birthdayValidation = validateBirthday(birthday)
        if (!birthdayValidation.valid) {
          setError(birthdayValidation.error)
          return false
        }
      }
    }

    // Password validation for both modes
    if (!password) {
      setError('Por favor, informe uma senha.')
      return false
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.')
      return false
    }

    if (password !== confirmPassword) {
      setError('As senhas nao conferem.')
      return false
    }

    return true
  }

  const handleRegister = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setError('')
    setLoading(true)

    try {
      // INVITE MODE: Set password for existing user
      if (isInviteMode) {
        const result = await authService.setPassword(inviteToken, password)

        if (result && result.access_token) {
          setSuccess(true)
          await login(result)
          setTimeout(() => {
            navigate(ROUTES.DASHBOARD)
          }, 1500)
        } else {
          setError('Erro ao definir senha. Tente novamente.')
        }
        return
      }

      // REGISTER MODE: Create new account
      const registrationData = {
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone_number: phoneNumber ? normalizePhoneNumber(phoneNumber) : null,
        date_of_birth: formatBirthdayForBackend(birthday)
      }

      const result = await authService.register(registrationData)

      if (result) {
        // Registration successful - now log the user in
        setSuccess(true)

        // Auto-login after registration
        const tokenData = await authService.login(email, password)
        if (tokenData) {
          await login(tokenData)
          navigate(ROUTES.DASHBOARD)
        } else {
          // If auto-login fails, redirect to login page
          setTimeout(() => {
            navigate(ROUTES.LOGIN)
          }, 2000)
        }
      } else {
        setError('Erro ao criar conta. Tente novamente.')
      }
    } catch (error) {
      // Handle specific error messages from backend
      if (isInviteMode) {
        if (error.message?.toLowerCase().includes('invalido') || error.message?.toLowerCase().includes('expirado')) {
          setError('Link invalido ou expirado. Solicite um novo convite ao salao.')
        } else {
          setError(error.message || 'Ocorreu um erro ao definir sua senha.')
        }
      } else {
        if (error.message?.includes('email')) {
          setError('Este e-mail ja esta cadastrado.')
        } else {
          setError(error.message || 'Ocorreu um erro ao criar sua conta.')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN)
  }

  if (success) {
    return (
      <div className="h-full flex flex-col bg-brand-dark dark:bg-brand-light">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 border-2 border-brand-light dark:border-brand-dark rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-brand-light dark:text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-serif font-bold text-brand-light dark:text-brand-dark mb-4">
              {isInviteMode ? 'Senha definida com sucesso!' : 'Conta criada com sucesso!'}
            </h2>
            <p className="text-brand-light/70 dark:text-brand-dark/70">
              Redirecionando para o aplicativo...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-brand-dark dark:bg-brand-light">
      {/* Header - Luxury Design */}
      <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 text-center flex-shrink-0 border-b border-brand-light/10 dark:border-brand-dark/10">
        <h1 className="text-4xl font-serif font-bold text-brand-light dark:text-brand-dark mb-4 tracking-wide">
          {companyName}
        </h1>
        <div className="text-center">
          <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm mb-2 tracking-wide uppercase">
            {isInviteMode ? 'Ja possui uma conta?' : 'Ja tem uma conta?'}
          </p>
          <button
            onClick={handleBackToLogin}
            className="text-brand-light dark:text-brand-dark text-sm font-medium uppercase tracking-widest border-b border-brand-light/30 dark:border-brand-dark/30 hover:border-brand-light dark:hover:border-brand-dark transition-all duration-300"
          >
            Fazer login
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 overflow-y-auto overflow-x-hidden mobile-scroll min-h-0">
        {/* Header Text */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide uppercase">
            {isInviteMode ? 'Defina sua senha' : 'Criar sua conta'}
          </h2>
          {isInviteMode && (
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
              Crie uma senha segura para acessar o aplicativo
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 border border-red-600/20 dark:border-red-400/20 bg-transparent">
            <p className="text-red-600 dark:text-red-400 text-sm text-center tracking-wide">{error}</p>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleRegister} className="space-y-6">
          {/* Registration-only fields - hidden in invite mode */}
          {!isInviteMode && (
            <>
              {/* Full Name Field */}
              <div>
                <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Nome Completo *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                    <User size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Seu nome completo"
                    className="w-full pl-12 pr-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                    autoCapitalize="words"
                    autoCorrect="off"
                    autoComplete="name"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  E-mail *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                    <Mail size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="w-full pl-12 pr-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Phone Field (Optional) */}
              <div>
                <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Telefone (WhatsApp)
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                    <Phone size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(11) 99999-9999"
                    className="w-full pl-12 pr-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="tel"
                  />
                </div>
              </div>

              {/* Birthday Field (Optional) */}
              <div>
                <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
                  Aniversario (Dia/Mes)
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                    <Calendar size={18} strokeWidth={1.5} />
                  </div>
                  <input
                    type="text"
                    value={birthday}
                    onChange={(e) => setBirthday(handleBirthdayInput(e.target.value))}
                    placeholder="DD/MM (ex: 15/03)"
                    maxLength={5}
                    className="w-full pl-12 pr-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                    autoCapitalize="none"
                    autoCorrect="off"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </>
          )}

          {/* Password Field */}
          <div>
            <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Senha *
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                <Lock size={18} strokeWidth={1.5} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimo 6 caracteres"
                className="w-full pl-12 pr-12 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40 hover:text-brand-dark dark:hover:text-brand-light transition-all duration-300"
              >
                {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Confirmar Senha *
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                <Lock size={18} strokeWidth={1.5} />
              </div>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua senha"
                className="w-full pl-12 pr-12 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40 hover:text-brand-dark dark:hover:text-brand-light transition-all duration-300"
              >
                {showConfirmPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Register/Set Password Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark text-sm font-medium border border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 uppercase tracking-widest"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin"></div>
            ) : (
              isInviteMode ? 'Definir Senha' : 'Criar Conta'
            )}
          </button>

          {/* Terms/Info note */}
          <div className="text-center mt-4 pb-8">
            <p className="text-brand-dark/50 dark:text-brand-light/50 text-xs tracking-wide">
              {isInviteMode
                ? 'Apos definir sua senha, voce sera automaticamente conectado ao aplicativo.'
                : 'Ao criar sua conta, voce concorda com nossos termos de uso e politica de privacidade.'
              }
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RegisterPage
